/**
 * Calendario de mensualidades del estudiante.
 *
 * Muestra los doce meses del ciclo con su estado, capital, mora y días acumulados.
 * Tiene DOS modos que nunca se mezclan:
 *
 *   · Selección para PAGAR    — meses con saldo, respetando el orden obligatorio.
 *   · Selección para EXONERAR — solo meses con mora, solo Administración.
 *
 * Confundir ambos sería confundir cobrar con condonar, así que el modo se elige
 * explícitamente y cada uno habilita un conjunto distinto de meses.
 *
 * Requiere conexión: la mora se calcula contra los datos frescos del servidor. Sin
 * conexión la pantalla lo dice y se cae a los conceptos pendientes locales.
 */

import { useCallback, useEffect, useState } from "react";
import type { StudentCalendarDto, WaiverPreviewDto } from "@senm/contracts";
import {
  checkMonthlySelectionOrder,
  completeMonthlySelection,
  formatMoney,
  money,
  monthPeriod,
  newId,
  type CalendarMonth,
  type MonthlyChargeState,
} from "@senm/domain";
import { ApiRequestError, apiRequest } from "../api/client.js";
import { db } from "../local/db.js";
import type { StoredSession } from "../local/session.js";

type Modo = "PAGAR" | "EXONERAR";

/**
 * Tono visual de cada estado del mes.
 *
 * El símbolo y el texto siguen siendo la fuente de verdad —el color nunca comunica
 * solo—, pero en una tabla de doce filas el tono permite ubicar los meses vencidos
 * sin leer renglón por renglón.
 */
const TONO_POR_ESTADO: Readonly<Record<string, string>> = Object.freeze({
  PAGADO: "ok",
  VENCIDO: "error",
  PENDIENTE: "aviso",
  PARCIAL: "aviso",
  FUTURO: "neutro",
  SIN_CARGO: "neutro",
  CANCELADO: "neutro",
});

/**
 * Reconstruye la forma que espera la regla de orden del dominio a partir del DTO.
 * Se usa la MISMA función que valida el servidor, así la interfaz no puede permitir
 * una selección que el servidor vaya a rechazar.
 */
function aCalendarMonth(
  dto: StudentCalendarDto,
  mes: StudentCalendarDto["months"][number],
): CalendarMonth {
  const currency = dto.currency;
  return {
    cycleId: dto.cycleId,
    cycleYear: dto.cycleYear,
    month: mes.month,
    period: monthPeriod(dto.cycleYear, mes.month),
    chargeId: mes.chargeId,
    status: mes.status,
    originalAmount: money(mes.originalAmountMinor, currency),
    capitalOutstanding: money(mes.capitalOutstandingMinor, currency),
    lateFee: money(mes.lateFeeMinor, currency),
    paid: money(mes.paidMinor, currency),
    totalOutstanding: money(mes.totalOutstandingMinor, currency),
    lateFeeWaived: mes.lateFeeWaived,
    selectableForPayment: mes.selectableForPayment,
    selectableForWaiver: mes.selectableForWaiver,
  };
}

/** Un mes elegido para cobrar, con su desglose ya calculado por el servidor. */
export interface MesElegido {
  chargeId: string;
  monthLabel: string;
  statusLabel: string;
  statusSymbol: string;
  capitalMinor: number;
  lateFeeMinor: number;
  lateFeeDays: number;
  totalMinor: number;
  /**
   * Estado de mora reconstruido tal cual lo tiene el servidor.
   *
   * Viaja hasta el formulario de cobro porque la validación local usa la MISMA
   * función del dominio (`chargeOutstanding`) que el servidor: sin este estado,
   * el dispositivo solo ve el capital y rechaza un cobro legítimo diciendo que
   * «supera el saldo pendiente», ignorando la mora que él mismo acaba de mostrar.
   */
  estadoMora: MonthlyChargeState;
}

/** Reconstruye el estado de mora del mes a partir del DTO del calendario. */
function aEstadoMora(
  dto: StudentCalendarDto,
  mes: StudentCalendarDto["months"][number],
): MonthlyChargeState {
  const currency = dto.currency;
  return {
    originalAmount: money(mes.originalAmountMinor, currency),
    lateFeeStartDate: mes.lateFeeStartDate,
    lateFeeRatePerMille: mes.lateFeeRatePerMille,
    // El capital ya cubierto es el original menos lo que sigue pendiente.
    capitalPaid: money(
      mes.originalAmountMinor - mes.capitalOutstandingMinor,
      currency,
    ),
    lateFeePaid: money(mes.lateFeePaidMinor, currency),
    capitalSettledOn: mes.capitalSettledOn,
    lateFeeWaived: mes.lateFeeWaived,
    cancelled: mes.status === "CANCELADO",
  };
}

/**
 * Lo que el calendario le cuenta al formulario de pago.
 *
 * Viaja el DESGLOSE completo, no solo los identificadores: el cajero tiene al
 * cliente enfrente preguntando «¿y esos Q2,089.50 de qué son?», y esa respuesta
 * tiene que estar en la misma pantalla donde se teclea el importe.
 */
/**
 * Un concepto sin mes marcado para cobrar: inscripción, material, excursión.
 *
 * No lleva estado de mora porque no la genera: la mora es de las mensualidades.
 * Por eso no puede reutilizar `MesElegido` — obligaría a inventarle un estado
 * falso al formulario de cobro, que valida con la función del dominio.
 */
export interface CargoElegido {
  chargeId: string;
  conceptName: string;
  dueDate: string | null;
  totalMinor: number;
}

export interface SeleccionCalendario {
  currency: string;
  cycleName: string;
  elegidos: MesElegido[];
  /** Conceptos sueltos marcados. Van aparte porque no siguen el orden mensual. */
  otrosElegidos: CargoElegido[];
  totalMinor: number;
  /** Meses del ciclo ya saldados, para poder decirlo sin que haya que buscarlo. */
  mesesPagados: string[];
  /** Meses con saldo que NO se eligieron: lo que quedará debiendo. */
  mesesPendientes: string[];
}

export const SELECCION_VACIA: SeleccionCalendario = {
  currency: "GTQ",
  cycleName: "",
  elegidos: [],
  otrosElegidos: [],
  totalMinor: 0,
  mesesPagados: [],
  mesesPendientes: [],
};

/**
 * Descuenta del calendario del servidor los pagos que aún no han subido.
 *
 * El calendario lo calcula el servidor, que no sabe nada de un cobro registrado
 * hace diez segundos y todavía en la cola. Sin este ajuste, los meses recién
 * cobrados volvían a aparecer vencidos y con su casilla disponible: el cajero
 * podía cobrarlos DOS VECES, y el segundo cobro era tan válido como el primero.
 *
 * Solo se descuentan los pagos locales que siguen pendientes de subir. En cuanto
 * sincronizan, la cifra ya viene del servidor y volver a restarla aquí la contaría
 * dos veces.
 */
async function conPagosSinSubir(
  calendario: StudentCalendarDto,
  studentId: string,
): Promise<StudentCalendarDto> {
  const locales = await db.payments
    .where("studentId")
    .equals(studentId)
    .toArray();
  const porCargo = new Map<string, number>();
  for (const pago of locales) {
    if (pago.status !== "PENDIENTE") continue;
    for (const asignacion of pago.allocations) {
      porCargo.set(
        asignacion.chargeId,
        (porCargo.get(asignacion.chargeId) ?? 0) + asignacion.amountMinor,
      );
    }
  }
  if (porCargo.size === 0) return calendario;

  let totalDescontado = 0;

  const months = calendario.months.map((mes) => {
    const abonado =
      mes.chargeId === null ? 0 : (porCargo.get(mes.chargeId) ?? 0);
    if (abonado === 0) return mes;

    // El abono cubre primero la mora y luego el capital, igual que en el servidor.
    const moraCubierta = Math.min(mes.lateFeeMinor, abonado);
    const capitalCubierto = Math.min(
      mes.capitalOutstandingMinor,
      abonado - moraCubierta,
    );
    const pendiente =
      mes.totalOutstandingMinor - moraCubierta - capitalCubierto;
    totalDescontado += moraCubierta + capitalCubierto;

    return {
      ...mes,
      capitalOutstandingMinor: mes.capitalOutstandingMinor - capitalCubierto,
      lateFeeMinor: mes.lateFeeMinor - moraCubierta,
      paidMinor: mes.paidMinor + abonado,
      totalOutstandingMinor: pendiente,
      // Un mes ya cubierto deja de ser cobrable aunque el servidor aún lo crea vivo.
      selectableForPayment: pendiente > 0 && mes.selectableForPayment,
      selectableForWaiver: pendiente > 0 && mes.selectableForWaiver,
      statusLabel:
        pendiente === 0 ? "Pagado (pendiente de sincronizar)" : mes.statusLabel,
    };
  });

  const otherCharges = calendario.otherCharges.map((cargo) => {
    const abonado = porCargo.get(cargo.chargeId) ?? 0;
    if (abonado === 0) return cargo;
    const pendiente = Math.max(0, cargo.outstandingMinor - abonado);
    totalDescontado += cargo.outstandingMinor - pendiente;
    return {
      ...cargo,
      outstandingMinor: pendiente,
      paidMinor: cargo.paidMinor + abonado,
      selectableForPayment: pendiente > 0 && cargo.selectableForPayment,
    };
  });

  return {
    ...calendario,
    months,
    otherCharges,
    totalOutstandingMinor: Math.max(
      0,
      calendario.totalOutstandingMinor - totalDescontado,
    ),
  };
}

export function StudentCalendar(props: {
  session: StoredSession;
  studentId: string;
  cycleId: string;
  conexion: string;
  /** Se avisa al contenedor cuando cambia la selección de meses a pagar. */
  onSeleccionPago?: (seleccion: SeleccionCalendario) => void;
  onCambio?: () => Promise<void>;
  /**
   * Cambia cada vez que algo pudo alterar el calendario: un cobro, una exoneración
   * o una sincronización. Sin esto el calendario se cargaba UNA vez y seguía
   * mostrando como vencidos meses que se acababan de pagar.
   */
  recarga?: number;
}): JSX.Element {
  const [calendario, setCalendario] = useState<StudentCalendarDto | null>(null);
  const [modo, setModo] = useState<Modo>("PAGAR");
  const [seleccion, setSeleccion] = useState<string[]>([]);
  /**
   * Conceptos sueltos marcados, en su propia lista.
   *
   * Aparte de `seleccion` a propósito: el orden obligatorio y la exoneración de
   * mora solo tienen sentido sobre mensualidades, y mezclarlos obligaría a filtrar
   * unos de otros en cada regla, que es justo donde se cuelan los errores.
   */
  const [seleccionOtros, setSeleccionOtros] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  /**
   * Mora que se acuerda seguir cobrando, por mensualidad, en texto.
   *
   * Vacío o cero es la exoneración total de siempre. Se guarda como texto y no
   * como número porque el campo se está escribiendo: convertirlo en cada tecla
   * impediría borrar el contenido para teclear otra cifra.
   */
  const [acordado, setAcordado] = useState<Record<string, string>>({});
  const [previaExoneracion, setPreviaExoneracion] =
    useState<WaiverPreviewDto | null>(null);
  /**
   * Cargos que se van a exonerar AHORA. Es independiente de `seleccion`, que sirve
   * para cobrar: mezclarlos hacía que exonerar un mes borrara lo marcado para pagar.
   */
  const [exoneracionDe, setExoneracionDe] = useState<string[] | null>(null);
  const [ciclos, setCiclos] = useState<
    { id: string; name: string; closed: boolean }[]
  >([]);
  /** Ciclo que se está viendo. Empieza en el del estudiante y puede cambiarse. */
  const [cicloVisto, setCicloVisto] = useState(props.cycleId);

  const puedeExonerar = props.session.user.permissions.includes(
    "conflictos:resolver",
  );

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const delServidor = await apiRequest<StudentCalendarDto>(
        `/v1/students/${props.studentId}/calendar/${cicloVisto}`,
      );
      const ajustado = await conPagosSinSubir(delServidor, props.studentId);
      setCalendario(ajustado);

      // Se descarta de la selección lo que ya dejó de ser cobrable. Tras un cobro la
      // casilla quedaba MARCADA aunque el mes ya estuviera pagado: deshabilitada,
      // pero marcada, y el total seguía contándola. Se filtra en vez de vaciar
      // porque esto también corre al sincronizar, y vaciar borraría lo que el cajero
      // estuviera marcando en ese momento.
      const cobrables = new Set<string>([
        ...ajustado.months
          .filter((mes) => mes.selectableForPayment && mes.chargeId !== null)
          .map((mes) => mes.chargeId as string),
        ...ajustado.otherCharges
          .filter((cargo) => cargo.selectableForPayment)
          .map((cargo) => cargo.chargeId),
      ]);
      setSeleccion((previa) => previa.filter((id) => cobrables.has(id)));
      // Los conceptos sueltos se depuran igual: uno ya cobrado deja de ser cobrable
      // y no puede quedarse marcado sumando al total.
      setSeleccionOtros((previa) => previa.filter((id) => cobrables.has(id)));
    } catch (problema) {
      setError(
        problema instanceof ApiRequestError && problema.isNetworkFailure
          ? "El calendario y la mora necesitan conexión: se calculan con los datos del servidor."
          : "No fue posible cargar el calendario.",
      );
      setCalendario(null);
    } finally {
      setCargando(false);
    }
  }, [props.studentId, cicloVisto, props.recarga]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // El contrato exige poder cobrar el ciclo SIGUIENTE por anticipado, así que hay
  // que poder mirarlo aunque el estudiante no tenga asignación académica en él.
  useEffect(() => {
    void (async () => {
      try {
        const datos = await apiRequest<{
          cycles: { id: string; name: string; closed: boolean }[];
        }>("/v1/cycles");
        setCiclos(datos.cycles);
      } catch {
        // Sin la lista, el calendario sigue mostrando el ciclo del estudiante.
      }
    })();
  }, []);

  /**
   * Se avisa al contenedor cada vez que cambia algo que altere el cobro.
   *
   * Un solo efecto en vez de un aviso en cada botón: antes había cuatro llamadas
   * repartidas y bastaba olvidar una —cambiar de ciclo, por ejemplo— para que el
   * formulario de abajo siguiera mostrando meses de otro año.
   *
   * En modo EXONERAR se emite vacío: exonerar no es cobrar, y arrastrar esa
   * selección al formulario de pago cobraría lo que se pretendía condonar.
   */
  const onSeleccionPago = props.onSeleccionPago;
  useEffect(() => {
    if (onSeleccionPago === undefined) return;
    if (calendario === null || modo !== "PAGAR") {
      onSeleccionPago(SELECCION_VACIA);
      return;
    }

    const elegidos: MesElegido[] = [];
    const mesesPagados: string[] = [];
    const mesesPendientes: string[] = [];

    for (const mes of calendario.months) {
      if (mes.status === "PAGADO") {
        mesesPagados.push(mes.monthLabel);
        continue;
      }
      if (mes.chargeId === null || mes.totalOutstandingMinor === 0) continue;

      if (seleccion.includes(mes.chargeId)) {
        elegidos.push({
          chargeId: mes.chargeId,
          monthLabel: mes.monthLabel,
          statusLabel: mes.statusLabel,
          statusSymbol: mes.statusSymbol,
          capitalMinor: mes.capitalOutstandingMinor,
          lateFeeMinor: mes.lateFeeMinor,
          lateFeeDays: mes.lateFeeDays,
          totalMinor: mes.totalOutstandingMinor,
          estadoMora: aEstadoMora(calendario, mes),
        });
      } else {
        mesesPendientes.push(mes.monthLabel);
      }
    }

    const otrosElegidos: CargoElegido[] = calendario.otherCharges
      .filter(
        (cargo) =>
          seleccionOtros.includes(cargo.chargeId) && cargo.outstandingMinor > 0,
      )
      .map((cargo) => ({
        chargeId: cargo.chargeId,
        conceptName: cargo.conceptName,
        dueDate: cargo.dueDate,
        totalMinor: cargo.outstandingMinor,
      }));

    onSeleccionPago({
      currency: calendario.currency,
      cycleName: calendario.cycleName,
      elegidos,
      otrosElegidos,
      totalMinor:
        elegidos.reduce((acc, mes) => acc + mes.totalMinor, 0) +
        otrosElegidos.reduce((acc, cargo) => acc + cargo.totalMinor, 0),
      mesesPagados,
      mesesPendientes,
    });
  }, [calendario, seleccion, seleccionOtros, modo, onSeleccionPago]);

  // Cambiar de modo limpia la selección: nunca se arrastra una selección de pago
  // hacia una exoneración.
  const cambiarModo = (nuevo: Modo): void => {
    setModo(nuevo);
    setSeleccion([]);
    setSeleccionOtros([]);
    setPreviaExoneracion(null);
    setAviso(null);
  };

  const alternar = (chargeId: string): void => {
    setPreviaExoneracion(null);
    setSeleccion((previa) => {
      const siguiente = previa.includes(chargeId)
        ? previa.filter((id) => id !== chargeId)
        : [...previa, chargeId];
      return siguiente;
    });
  };

  if (cargando && calendario === null)
    return <p role="status">Cargando calendario…</p>;

  if (calendario === null) {
    return (
      <div className="aviso-caja" role="status">
        {error ?? "El calendario no está disponible."}
      </div>
    );
  }

  const meses = calendario.months.map((mes) => aCalendarMonth(calendario, mes));
  const revision = checkMonthlySelectionOrder(meses, seleccion);

  /**
   * Lo que suman las DOS tablas.
   *
   * Los conceptos sueltos solo cuentan al cobrar: no generan mora, asi que en modo
   * EXONERAR no hay nada suyo que perdonar. Sumando solo los meses, marcar la
   * inscripcion sola anunciaba "1 concepto seleccionado · Total a pagar: Q0.00".
   */
  const totalSeleccionado =
    calendario.months
      .filter(
        (mes) => mes.chargeId !== null && seleccion.includes(mes.chargeId),
      )
      .reduce(
        (acc, mes) =>
          acc +
          (modo === "PAGAR" ? mes.totalOutstandingMinor : mes.lateFeeMinor),
        0,
      ) +
    (modo === "PAGAR"
      ? calendario.otherCharges
          .filter((cargo) => seleccionOtros.includes(cargo.chargeId))
          .reduce((acc, cargo) => acc + cargo.outstandingMinor, 0)
      : 0);

  const completarSeleccion = (): void => {
    const completa = completeMonthlySelection(meses, seleccion);
    setSeleccion(completa);
  };

  /**
   * Exonera la mora de UN mes desde su propia fila.
   *
   * NO toca el modo ni la selección de cobro, y eso es el punto. La primera versión
   * cambiaba a modo EXONERAR, con dos consecuencias que rompían el trabajo del
   * cajero: los meses que ya tenía marcados para cobrar se descartaban —el botón
   * «Cobrar N meses» simplemente desaparecía—, y el modo se quedaba pegado, así
   * que las casillas que marcara después se entendían como exoneraciones.
   *
   * Exonerar la mora de un mes es una acción puntual sobre ESE mes. Se resuelve
   * en su propio diálogo y devuelve la pantalla exactamente como estaba.
   */
  const exonerarSoloEste = async (chargeId: string): Promise<void> => {
    setAviso(null);
    setError(null);
    setExoneracionDe([chargeId]);
    try {
      setPreviaExoneracion(
        await apiRequest<WaiverPreviewDto>("/v1/late-fee-waivers/preview", {
          method: "POST",
          body: {
            studentId: props.studentId,
            chargeIds: [chargeId],
            agreed: acuerdoEnMinimos(),
          },
        }),
      );
    } catch (problema) {
      setExoneracionDe(null);
      setError(
        problema instanceof ApiRequestError
          ? problema.message
          : "No fue posible calcular la exoneración.",
      );
    }
  };

  /** Convierte lo tecleado a unidades mínimas; lo ilegible cuenta como cero. */
  const acuerdoEnMinimos = (): Record<string, number> => {
    const salida: Record<string, number> = {};
    for (const [chargeId, texto] of Object.entries(acordado)) {
      const limpio = texto.trim().replace(",", ".");
      if (limpio === "") continue;
      const valor = Number(limpio);
      if (!Number.isFinite(valor) || valor <= 0) continue;
      salida[chargeId] = Math.round(valor * 100);
    }
    return salida;
  };

  /**
   * Vuelve a pedir la vista previa con lo tecleado.
   *
   * Hace falta porque el importe acordado se escribe DESPUÉS de calcular la
   * previa: sin recalcular, la columna de «mora a exonerar» mostraría una cifra
   * vieja y el token de la previa ya no coincidiría con lo que se confirma, así
   * que el servidor lo rechazaría con razón.
   */
  const recalcularPrevia = async (): Promise<void> => {
    if (exoneracionDe === null) return;
    setError(null);
    try {
      setPreviaExoneracion(
        await apiRequest<WaiverPreviewDto>("/v1/late-fee-waivers/preview", {
          method: "POST",
          body: {
            studentId: props.studentId,
            chargeIds: exoneracionDe,
            agreed: acuerdoEnMinimos(),
          },
        }),
      );
    } catch (problema) {
      setError(
        problema instanceof ApiRequestError
          ? problema.message
          : "No fue posible recalcular la exoneración.",
      );
    }
  };

  /** Cierra el diálogo de exoneración dejando todo lo demás intacto. */
  const cerrarExoneracion = (): void => {
    setPreviaExoneracion(null);
    setExoneracionDe(null);
    setAcordado({});
  };

  const calcularExoneracion = async (): Promise<void> => {
    setError(null);
    setExoneracionDe(seleccion);
    try {
      setPreviaExoneracion(
        await apiRequest<WaiverPreviewDto>("/v1/late-fee-waivers/preview", {
          method: "POST",
          body: {
            studentId: props.studentId,
            chargeIds: seleccion,
            agreed: acuerdoEnMinimos(),
          },
        }),
      );
    } catch (problema) {
      setError(
        problema instanceof ApiRequestError
          ? problema.message
          : "No fue posible calcular la exoneración.",
      );
    }
  };

  const confirmarExoneracion = async (): Promise<void> => {
    if (previaExoneracion === null || exoneracionDe === null) return;
    setError(null);
    try {
      const resultado = await apiRequest<{
        monthsWaived: number;
        totalWaivedMinor: number;
      }>("/v1/late-fee-waivers/confirm", {
        method: "POST",
        body: {
          studentId: props.studentId,
          chargeIds: exoneracionDe,
          agreed: acuerdoEnMinimos(),
          previewToken: previaExoneracion.previewToken,
          operationId: newId(),
        },
      });
      setAviso(
        `Se exoneró la mora de ${resultado.monthsWaived} mes(es) por ` +
          `${formatMoney(money(resultado.totalWaivedMinor, calendario.currency))}. El capital no se modificó.`,
      );
      // La selección de cobro solo se limpia si la exoneración salió de ella.
      if (modo === "EXONERAR") setSeleccion([]);
      cerrarExoneracion();
      await cargar();
      await props.onCambio?.();
    } catch (problema) {
      setError(
        problema instanceof ApiRequestError
          ? problema.message
          : "No fue posible exonerar la mora.",
      );
      cerrarExoneracion();
    }
  };

  return (
    <>
      <section aria-labelledby="titulo-calendario">
        <h2 id="titulo-calendario">Calendario de mensualidades</h2>

        {ciclos.length > 1 && (
          <label className="selector-ciclo">
            Ciclo escolar
            <select
              value={cicloVisto}
              onChange={(evento) => {
                setCicloVisto(evento.target.value);
                // Cambiar de ciclo limpia la selección: mezclar meses de dos ciclos
                // en una misma selección visual confundiría lo que se va a cobrar.
                setSeleccion([]);
                setPreviaExoneracion(null);
              }}
            >
              {ciclos.map((ciclo) => (
                <option key={ciclo.id} value={ciclo.id}>
                  {ciclo.name}
                  {ciclo.closed ? " (cerrado)" : ""}
                </option>
              ))}
            </select>
          </label>
        )}

        {calendario.assignmentPending && (
          <p className="aviso-caja" role="status">
            <strong>Pendiente de asignación académica.</strong> El estudiante
            todavía no tiene grado y sección en este ciclo. Puede cobrar igual:
            el pago queda ligado al estudiante y al ciclo.
          </p>
        )}

        {aviso !== null && (
          <div className="aviso-caja ok" role="status">
            {aviso}
          </div>
        )}
        {error !== null && (
          <div className="aviso-caja error" role="alert">
            {error}
          </div>
        )}

        {puedeExonerar && (
          <fieldset className="modos">
            <legend>Qué desea hacer</legend>
            <label className="casilla">
              <input
                type="radio"
                name="modo-calendario"
                checked={modo === "PAGAR"}
                onChange={() => cambiarModo("PAGAR")}
              />
              Seleccionar meses para <strong>pagar</strong>
            </label>
            <label className="casilla">
              <input
                type="radio"
                name="modo-calendario"
                checked={modo === "EXONERAR"}
                onChange={() => cambiarModo("EXONERAR")}
              />
              Seleccionar meses para <strong>exonerar mora</strong>
            </label>
          </fieldset>
        )}

        <div className="tabla-desplazable">
          <table className="tabla">
            <caption className="oculto-visual">
              Doce meses del ciclo {calendario.cycleName} con su estado, capital
              y mora
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  <span className="oculto-visual">Seleccionar</span>
                </th>
                <th scope="col">Mes</th>
                <th scope="col">Estado</th>
                <th scope="col" className="numero">
                  Capital
                </th>
                <th scope="col" className="numero">
                  Mora
                </th>
                <th scope="col" className="numero">
                  Días
                </th>
                <th scope="col" className="numero">
                  Total
                </th>
                <th scope="col">Vence</th>
                {puedeExonerar && <th scope="col">Acción</th>}
              </tr>
            </thead>
            <tbody>
              {calendario.months.map((mes) => {
                const habilitado =
                  mes.chargeId !== null &&
                  (modo === "PAGAR"
                    ? mes.selectableForPayment
                    : mes.selectableForWaiver);
                const elegido =
                  mes.chargeId !== null && seleccion.includes(mes.chargeId);
                const faltante = revision.missing.some(
                  (m) => m.chargeId === mes.chargeId,
                );

                return (
                  <tr
                    key={mes.month}
                    className={faltante ? "fila-faltante" : undefined}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={elegido}
                        disabled={!habilitado}
                        onChange={() =>
                          mes.chargeId !== null && alternar(mes.chargeId)
                        }
                        aria-label={`${modo === "PAGAR" ? "Pagar" : "Exonerar mora de"} ${mes.monthLabel}`}
                      />
                    </td>
                    <th scope="row">{mes.monthLabel}</th>
                    <td>
                      {/* Símbolo + texto: el estado nunca depende solo del color. */}
                      <span
                        className={`marca-estado ${TONO_POR_ESTADO[mes.status] ?? "neutro"}`}
                      >
                        <span aria-hidden="true">{mes.statusSymbol}</span>
                        {mes.statusLabel}
                      </span>
                      {mes.lateFeeWaived && (
                        <span className="ayuda"> · mora exonerada</span>
                      )}
                    </td>
                    <td className="numero">
                      {mes.chargeId === null
                        ? "—"
                        : formatMoney(
                            money(
                              mes.capitalOutstandingMinor,
                              calendario.currency,
                            ),
                          )}
                    </td>
                    <td className="numero">
                      {mes.lateFeeMinor === 0
                        ? "—"
                        : formatMoney(
                            money(mes.lateFeeMinor, calendario.currency),
                          )}
                    </td>
                    <td className="numero">
                      {mes.lateFeeDays === 0 ? "—" : mes.lateFeeDays}
                    </td>
                    <td className="numero">
                      {mes.chargeId === null
                        ? "—"
                        : formatMoney(
                            money(
                              mes.totalOutstandingMinor,
                              calendario.currency,
                            ),
                          )}
                    </td>
                    <td>{mes.chargeId === null ? "—" : mes.dueDate}</td>
                    {puedeExonerar && (
                      <td>
                        {mes.selectableForWaiver ? (
                          <button
                            type="button"
                            className="boton compacto"
                            onClick={() =>
                              void exonerarSoloEste(mes.chargeId as string)
                            }
                          >
                            Exonerar mora
                          </button>
                        ) : (
                          <span className="ayuda">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Orden obligatorio: se avisa y se ofrece completar, en vez de fallar al enviar. */}
        {modo === "PAGAR" && !revision.valid && (
          <div className="aviso-caja" role="alert">
            <strong>Falta cubrir meses anteriores.</strong> {revision.message}{" "}
            <button
              type="button"
              className="boton"
              onClick={completarSeleccion}
            >
              Agregarlos a la selección
            </button>
          </div>
        )}

        {(seleccion.length > 0 || seleccionOtros.length > 0) && (
          <p className="resumen-seleccion">
            {seleccion.length} mes(es)
            {seleccionOtros.length > 0
              ? ` y ${seleccionOtros.length} concepto(s)`
              : ""}{" "}
            seleccionado(s) ·{" "}
            <strong>
              {modo === "PAGAR" ? "Total a pagar: " : "Mora a exonerar: "}
              {formatMoney(money(totalSeleccionado, calendario.currency))}
            </strong>
          </p>
        )}

        {/* --- Exoneración -------------------------------------------------- */}
        {modo === "EXONERAR" &&
          seleccion.length > 0 &&
          previaExoneracion === null && (
            <button
              type="button"
              className="boton"
              onClick={() => void calcularExoneracion()}
            >
              Calcular exoneración
            </button>
          )}

        {previaExoneracion !== null && (
          <div
            className="tarjeta"
            role="dialog"
            aria-labelledby="titulo-exoneracion"
          >
            <h3 id="titulo-exoneracion">Confirmar exoneración de mora</h3>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Mes</th>
                  <th scope="col" className="numero">
                    Días de mora
                  </th>
                  <th scope="col" className="numero">
                    Mora a cobrar
                  </th>
                  <th scope="col" className="numero">
                    Mora a exonerar
                  </th>
                </tr>
              </thead>
              <tbody>
                {previaExoneracion.lines.map((linea) => (
                  <tr key={linea.chargeId}>
                    <th scope="row">{linea.monthLabel}</th>
                    <td className="numero">{linea.lateFeeDays}</td>
                    <td className="numero">
                      <input
                        value={acordado[linea.chargeId] ?? ""}
                        onChange={(evento) =>
                          setAcordado({
                            ...acordado,
                            [linea.chargeId]: evento.target.value,
                          })
                        }
                        onBlur={() => void recalcularPrevia()}
                        inputMode="decimal"
                        placeholder="0.00"
                        aria-label={`Mora a cobrar de ${linea.monthLabel}`}
                      />
                    </td>
                    <td className="numero">
                      {formatMoney(
                        money(linea.waivedMinor, previaExoneracion.currency),
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" colSpan={3}>
                    Total exonerado
                  </th>
                  <td className="numero">
                    <strong>
                      {formatMoney(
                        money(
                          previaExoneracion.totalWaivedMinor,
                          previaExoneracion.currency,
                        ),
                      )}
                    </strong>
                  </td>
                </tr>
              </tfoot>
            </table>

            <div className="aviso-caja" role="alert">
              {previaExoneracion.notice}
            </div>

            <div className="acciones">
              <button
                type="button"
                className="boton primario"
                onClick={() => void confirmarExoneracion()}
              >
                Sí, exonerar la mora
              </button>
              <button type="button" onClick={cerrarExoneracion}>
                Cancelar
              </button>
            </div>
          </div>
        )}

        <dl className="resumen">
          <dt>Saldo total del ciclo</dt>
          <dd>
            {formatMoney(
              money(calendario.totalOutstandingMinor, calendario.currency),
            )}
          </dd>
          <dt>De ello, mora</dt>
          <dd>
            {formatMoney(
              money(calendario.totalLateFeeMinor, calendario.currency),
            )}
          </dd>
          <dt>Datos al</dt>
          <dd>{calendario.asOf}</dd>
        </dl>
      </section>
      {/*
        Conceptos sin mes: SECCION APARTE, no una tabla mas del calendario.

        No se mezclan con los meses aunque caigan en las mismas fechas: las
        mensualidades tienen orden obligatorio —no se paga septiembre debiendo
        agosto— y estos no lo tienen. En una sola tabla, el cajero daria por hecho
        que la misma regla se aplica a todo. Ademas no todos los alumnos los
        tienen: uno lleva recuperacion y el de al lado no.

        Fuera de la region del calendario a proposito: dentro, sus filas contaban
        como filas del calendario y la inscripcion se anunciaba como un mes mas.
      */}
      {modo === "PAGAR" && calendario.otherCharges.length > 0 && (
        <section aria-labelledby="titulo-otros-conceptos">
          <h2 id="titulo-otros-conceptos">Otros conceptos</h2>
          <div className="tabla-desplazable">
            <table className="tabla">
              <caption className="oculto-visual">
                Conceptos sin mes del estudiante, con su estado y su saldo
              </caption>
              <thead>
                <tr>
                  <th scope="col">
                    <span className="oculto-visual">Seleccionar</span>
                  </th>
                  <th scope="col">Concepto</th>
                  <th scope="col">Estado</th>
                  <th scope="col" className="numero">
                    Pendiente
                  </th>
                  <th scope="col">Vence</th>
                </tr>
              </thead>
              <tbody>
                {calendario.otherCharges.map((cargo) => (
                  <tr key={cargo.chargeId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={seleccionOtros.includes(cargo.chargeId)}
                        disabled={!cargo.selectableForPayment}
                        onChange={() =>
                          setSeleccionOtros((previa) =>
                            previa.includes(cargo.chargeId)
                              ? previa.filter((otro) => otro !== cargo.chargeId)
                              : [...previa, cargo.chargeId],
                          )
                        }
                        aria-label={`Pagar ${cargo.conceptName}`}
                      />
                    </td>
                    <th scope="row">{cargo.conceptName}</th>
                    <td>
                      <span className="marca-estado neutro">
                        {cargo.statusLabel}
                      </span>
                    </td>
                    <td className="numero">
                      {cargo.outstandingMinor === 0
                        ? "—"
                        : formatMoney(
                            money(cargo.outstandingMinor, calendario.currency),
                          )}
                    </td>
                    <td>{cargo.dueDate ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
